import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import type { ReactNode } from 'react';

export function AdminHeader({ kicker, title }: { kicker: string; title: string }) {
  return (
    <div>
      <p className="font-sans text-xs uppercase tracking-[0.18em] text-slate">{kicker}</p>
      <h1 className="mt-2 font-serif text-3xl text-ink sm:text-4xl">{title}</h1>
    </div>
  );
}

export function FilterTabs<T extends string>({
  options,
  value,
  onChange,
}: {
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1 border-b border-ink/10">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`-mb-px border-b-2 px-4 py-2 font-sans text-sm ${
            value === o.value
              ? 'border-oxblood text-ink'
              : 'border-transparent text-ink/60 hover:text-ink'
          }`}
          aria-pressed={value === o.value}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const tone: Record<string, string> = {
    pending: 'bg-slate/15 text-slate',
    pending_review: 'bg-slate/15 text-slate',
    approved: 'bg-emerald-600/10 text-emerald-700',
    published: 'bg-emerald-600/10 text-emerald-700',
    converted: 'bg-emerald-600/10 text-emerald-700',
    invited: 'bg-oxblood/10 text-oxblood',
    rejected: 'bg-oxblood/10 text-oxblood',
    declined: 'bg-oxblood/10 text-oxblood',
    draft: 'bg-ink/10 text-ink/60',
    unpublished: 'bg-ink/10 text-ink/60',
  };
  return (
    <span
      className={`rounded-sm px-2 py-0.5 font-sans text-[0.7rem] uppercase tracking-[0.12em] ${
        tone[status] ?? 'bg-ink/10 text-ink/60'
      }`}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

export function Drawer({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40" />
        <Dialog.Content className="fixed right-0 top-0 z-50 flex h-full w-full max-w-2xl flex-col overflow-y-auto bg-parchment shadow-2xl">
          <div className="sticky top-0 flex items-center justify-between border-b border-ink/10 bg-parchment px-6 py-4">
            <Dialog.Title className="font-serif text-xl text-ink">{title}</Dialog.Title>
            <Dialog.Close
              className="rounded-sm p-1 text-ink/50 hover:text-oxblood"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>
          <div className="px-6 py-6">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function NotesField({
  value,
  onChange,
  required,
  label = 'Reviewer notes',
}: {
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  label?: string;
}) {
  return (
    <div>
      <label className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
        {label}
        {required && <span className="text-oxblood"> · required</span>}
      </label>
      <textarea
        rows={4}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none focus:ring-2 focus:ring-oxblood/30"
      />
    </div>
  );
}
